import { useCallback, useMemo, useSyncExternalStore } from 'react';

import type { EntityChange } from './_generated';

export const RECENT_WINDOW_MS = 2000;

const expiries = new Map<string, number>();
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<string> = new Set();
let sweepTimer: ReturnType<typeof setTimeout> | null = null;

function publish(): void {
  snapshot = new Set(expiries.keys());
  listeners.forEach((listener) => listener());
}

function scheduleSweep(): void {
  if (sweepTimer !== null || expiries.size === 0) return;
  const nextExpiry = Math.min(...expiries.values());
  sweepTimer = setTimeout(sweep, Math.max(0, nextExpiry - Date.now()));
}

function sweep(): void {
  sweepTimer = null;
  const now = Date.now();
  let removed = false;
  expiries.forEach((expiresAt, entityId) => {
    if (expiresAt <= now) {
      expiries.delete(entityId);
      removed = true;
    }
  });
  if (removed) publish();
  scheduleSweep();
}

export function recordRecentChanges(changes: EntityChange[]): void {
  if (changes.length === 0) return;
  const expiresAt = Date.now() + RECENT_WINDOW_MS;
  changes.forEach((change) => expiries.set(change.entity.id, expiresAt));
  publish();
  scheduleSweep();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ReadonlySet<string> {
  return snapshot;
}

export function useRecentChanges(): { isRecent: (entityId: string) => boolean } {
  const recent = useSyncExternalStore(subscribe, getSnapshot);
  const isRecent = useCallback((entityId: string) => recent.has(entityId), [recent]);
  return useMemo(() => ({ isRecent }), [isRecent]);
}
